import React, { useCallback, useEffect, useRef, useState } from "react";
import { Group } from "@scrile/api-provider/dist/api/GroupsProvider";
import { Topic } from "@scrile/api-provider/dist/api/TopicsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { getFileMimeType } from "@scrile/tools/dist/lib/FileHelpers";
import { useTranslation } from "locales";
import getPublicOrigin from "lib/getPublicOrigin";
import useAuthUser from "hooks/useAuthUser";
import emitter, { EVENTS, SYSTEM_MESSAGE_PREFIX } from "lib/emitter";
import { MediaFilesController } from "lib/MediaFileController";
import { Routes } from "router/config";
import { stripSignature } from "lib/fileHelpers";
import fileUploader from "lib/fileUploader";
import { removeCachedThread } from "lib/offlineCache";
import providers from "lib/providers";
import BaseAvatar from "components/BaseAvatar";
import "./style.scss";

interface Props {
  group: Group;
  onClose: () => void;
  onMembersChanged?: (group: Group) => void;
  onLeaveGroup?: () => void;
  onDeleteGroup?: () => void;
  threadId?: string;
}

function GroupInfoPanel({ group, onClose, onMembersChanged, onLeaveGroup, onDeleteGroup, threadId }: Props) {
  const { t } = useTranslation();
  const { user: authUser } = useAuthUser();
  const [members, setMembers] = useState<UserPublicData[]>([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<UserPublicData[]>([]);
  const [searching, setSearching] = useState(false);
  const isOwner = authUser && group.ownerId === String(authUser.id);

  // Local toggle state (synced with group prop)
  const [localIsPublic, setLocalIsPublic] = useState(group.isPublic || false);
  const [localCallsEnabled, setLocalCallsEnabled] = useState(group.callsEnabled || false);
  const [localAutoRecord, setLocalAutoRecord] = useState(group.autoRecordVideo || false);
  const [localTopicsEnabled, setLocalTopicsEnabled] = useState(group.topicsEnabled || false);
  useEffect(() => { setLocalIsPublic(group.isPublic || false); }, [group.isPublic]);
  useEffect(() => { setLocalCallsEnabled(group.callsEnabled || false); }, [group.callsEnabled]);
  useEffect(() => { setLocalAutoRecord(group.autoRecordVideo || false); }, [group.autoRecordVideo]);
  useEffect(() => { setLocalTopicsEnabled(group.topicsEnabled || false); }, [group.topicsEnabled]);

  // Group Topics (Темы) management (owner-only). Loaded lazily when topics are on.
  const [topics, setTopics] = useState<Topic[]>([]);
  const [newTopicName, setNewTopicName] = useState("");
  const [topicBusy, setTopicBusy] = useState(false);
  const [editingTopicId, setEditingTopicId] = useState<string | null>(null);
  const [editingTopicName, setEditingTopicName] = useState("");

  const loadTopics = useCallback(async () => {
    try {
      const list = await providers.TopicsProvider.search(group.id);
      setTopics(list);
    } catch (e) { console.error("Failed to load topics:", e); }
  }, [group.id]);

  useEffect(() => {
    if (localTopicsEnabled) loadTopics();
  }, [localTopicsEnabled, loadTopics]);

  const handleToggleTopics = useCallback(async (enabled: boolean) => {
    setLocalTopicsEnabled(enabled);
    try {
      const updated = await providers.GroupsProvider.toggleTopics(group.id, enabled);
      if (onMembersChanged && updated) onMembersChanged(updated);
      emitter.emit(EVENTS.TOPICS_CHANGED);
    } catch (err) {
      setLocalTopicsEnabled(!enabled);
      console.error("Failed to toggle topics:", err);
    }
  }, [group.id, onMembersChanged]);

  const handleCreateTopic = useCallback(async () => {
    const name = newTopicName.trim();
    if (!name || topicBusy) return;
    setTopicBusy(true);
    try {
      await providers.TopicsProvider.create({ data: { groupId: group.id, name } });
      setNewTopicName("");
      await loadTopics();
      emitter.emit(EVENTS.TOPICS_CHANGED);
    } catch (e) { console.error("Failed to create topic:", e); }
    finally { setTopicBusy(false); }
  }, [newTopicName, topicBusy, group.id, loadTopics]);

  const handleRenameTopic = useCallback(async (topicId: string) => {
    const name = editingTopicName.trim();
    if (!name) { setEditingTopicId(null); return; }
    try {
      await providers.TopicsProvider.update({ data: { groupId: group.id, id: topicId, name } });
      setEditingTopicId(null);
      await loadTopics();
      emitter.emit(EVENTS.TOPICS_CHANGED);
    } catch (e) { console.error("Failed to rename topic:", e); }
  }, [editingTopicName, group.id, loadTopics]);

  const handleDeleteTopic = useCallback(async (topic: Topic) => {
    if (!window.confirm(t("Delete topic \"{{name}}\"?", { name: topic.name }))) return;
    try {
      await providers.TopicsProvider.delete({ groupId: group.id, id: topic.id });
      await loadTopics();
      emitter.emit(EVENTS.TOPICS_CHANGED);
    } catch (e) { console.error("Failed to delete topic:", e); }
  }, [group.id, loadTopics, t]);

  // Editing state
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState(group.name || "");
  const [editingDesc, setEditingDesc] = useState(false);
  const [editDesc, setEditDesc] = useState(group.description || "");
  const [saving, setSaving] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [localAvatarUrl, setLocalAvatarUrl] = useState<string | null>(group.avatarUrl || null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const descInputRef = useRef<HTMLTextAreaElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocalAvatarUrl(group.avatarUrl || null);
  }, [group.avatarUrl]);

  const resolveThreadId = useCallback(async (): Promise<string | null> => {
    if (threadId) return threadId;
    if (!authUser) return null;
    try {
      const threads = await providers.MessageThreadsProvider.find({
        data: { userId: String(authUser.id), subjectType: "group", subjectId: group.id, size: 1 },
      });
      return threads.result?.length > 0 ? threads.result[0].id : null;
    } catch { return null; }
  }, [threadId, authUser, group.id]);

  const sendSystemMessage = useCallback(async (text: string) => {
    try {
      const tid = await resolveThreadId();
      if (!tid) return;
      await providers.MessagesProvider.sendExtendedMessages({
        data: { text: `${SYSTEM_MESSAGE_PREFIX}${text}`, threadIds: [tid] },
      });
    } catch (e) { console.warn("Failed to send system message:", e); }
  }, [resolveThreadId]);

  useEffect(() => {
    if (editingName && nameInputRef.current) nameInputRef.current.focus();
  }, [editingName]);

  useEffect(() => {
    if (editingDesc && descInputRef.current) descInputRef.current.focus();
  }, [editingDesc]);

  const handleSaveName = useCallback(async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === group.name) {
      setEditingName(false);
      return;
    }
    setSaving(true);
    try {
      const updated = await providers.GroupsProvider.update({ data: { id: group.id, name: trimmed } });
      if (onMembersChanged && updated) onMembersChanged(updated);
      setEditingName(false);
    } catch (e) {
      console.error("Failed to update group name:", e);
    } finally {
      setSaving(false);
    }
  }, [editName, group.name, group.id, onMembersChanged]);

  const handleSaveDesc = useCallback(async () => {
    const trimmed = editDesc.trim();
    if (trimmed === (group.description || "")) {
      setEditingDesc(false);
      return;
    }
    setSaving(true);
    try {
      const updated = await providers.GroupsProvider.update({ data: { id: group.id, description: trimmed } });
      if (onMembersChanged && updated) onMembersChanged(updated);
      setEditingDesc(false);
    } catch (e) {
      console.error("Failed to update group description:", e);
    } finally {
      setSaving(false);
    }
  }, [editDesc, group, onMembersChanged]);

  const handleCopyInviteLink = useCallback(() => {
    // Use the unguessable slug (not the sequential numeric id) so invite links
    // can't be enumerated. PageJoinGroup resolves slug-or-id for old links.
    const link = `${getPublicOrigin()}${Routes.groupJoinById.replace(":groupId", group.slug || group.id)}`;
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }).catch(() => {
      // Fallback
      const input = document.createElement("input");
      input.value = link;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }, [group.id]);

  const loadMembers = useCallback(async () => {
    if (!group.memberIds || group.memberIds.length === 0) {
      setMembers([]);
      return;
    }
    try {
      const result = await providers.UserPublicDataProvider.search({
        data: { id: group.memberIds, size: group.memberIds.length },
      });
      setMembers(result.result?.filter(Boolean) || []);
    } catch (e) {
      console.error("Failed to load members:", e);
    }
  }, [group.memberIds]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const result = await providers.UserPublicDataProvider.search({
          data: { relevantTerm: searchQuery, size: 20 },
        });
        const existingIds = new Set(group.memberIds || []);
        setSearchResults(
          result.result?.filter((u: UserPublicData) => !existingIds.has(String(u.id))) || []
        );
      } catch (e) {
        console.error("Search failed:", e);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, group.memberIds]);

  const handleAddMember = useCallback(
    async (userId: string) => {
      try {
        await providers.GroupsProvider.updateMembers({
          data: [{ id: group.id, memberId: userId, status: true }],
        });
        // Send system message
        const addedUser = searchResults.find((u) => String(u.id) === userId);
        const addedName = addedUser?.screenName || "User";
        const adderName = authUser?.screenName || "";
        await sendSystemMessage(`${adderName} added ${addedName} to the group`);
        setSearchQuery("");
        setSearchResults([]);
        if (onMembersChanged) {
          const updated = await providers.GroupsProvider.search({ data: { id: group.id } });
          if (updated.length > 0) onMembersChanged(updated[0]);
        }
      } catch (e) {
        console.error("Failed to add member:", e);
      }
    },
    [group.id, onMembersChanged, searchResults, authUser, sendSystemMessage]
  );

  const handleRemoveMember = useCallback(
    async (userId: string) => {
      try {
        // Send system message before removing
        const removedUser = members.find((u) => String(u.id) === userId);
        const removedName = removedUser?.screenName || "User";
        await sendSystemMessage(`${removedName} was removed from the group`);
        await providers.GroupsProvider.updateMembers({
          data: [{ id: group.id, memberId: userId, status: false }],
        });
        if (onMembersChanged) {
          const updated = await providers.GroupsProvider.search({ data: { id: group.id } });
          if (updated.length > 0) onMembersChanged(updated[0]);
        }
      } catch (e) {
        console.error("Failed to remove member:", e);
      }
    },
    [group.id, onMembersChanged, members, sendSystemMessage]
  );

  const handleLeaveGroup = useCallback(async () => {
    if (!authUser) return;
    // Send system message BEFORE leaving so user is still a member (best-effort)
    try {
      await sendSystemMessage(`${authUser.screenName || "User"} left the group`);
    } catch { /* best-effort, don't block leave */ }
    try {
      await providers.GroupsProvider.leaveGroup(group.id);
    } catch (e) {
      console.error("Failed to leave group:", e);
      return; // Don't proceed if leave failed
    }
    // Hide the thread so it disappears from the chat list immediately
    try {
      if (threadId) {
        await providers.MessageThreadsProvider.hide({ id: threadId });
        removeCachedThread(String(threadId)).catch(() => {});
      } else {
        const threads = await providers.MessageThreadsProvider.find({
          data: { userId: String(authUser.id), subjectType: "group", subjectId: group.id, size: 1 },
        });
        if (threads.result?.length > 0) {
          await providers.MessageThreadsProvider.hide({ id: threads.result[0].id });
          removeCachedThread(String(threads.result[0].id)).catch(() => {});
        }
      }
    } catch (e) { console.warn("Failed to hide thread:", e); }
    emitter.emit(EVENTS.THREADS_REFRESH);
    if (onLeaveGroup) {
      onLeaveGroup();
    } else {
      onClose();
    }
  }, [group.id, authUser, threadId, sendSystemMessage, onClose, onLeaveGroup]);

  const handleDeleteGroup = useCallback(async () => {
    if (!window.confirm(t("Are you sure you want to delete this group?"))) return;
    try {
      await providers.GroupsProvider.delete(group.id);
    } catch (e) {
      console.error("Failed to delete group:", e);
      return; // Don't proceed if delete failed
    }
    // Permanently remove the thread from all lists (not just hide)
    try {
      if (threadId) {
        await providers.MessageThreadsProvider.remove({ id: threadId });
        removeCachedThread(String(threadId)).catch(() => {});
      } else if (authUser) {
        const threads = await providers.MessageThreadsProvider.find({
          data: { userId: String(authUser.id), subjectType: "group", subjectId: group.id, size: 1 },
        });
        if (threads.result?.length > 0) {
          await providers.MessageThreadsProvider.remove({ id: threads.result[0].id });
          removeCachedThread(String(threads.result[0].id)).catch(() => {});
        }
      }
    } catch (e) { console.warn("Failed to remove thread:", e); }
    emitter.emit(EVENTS.THREADS_REFRESH);
    if (onDeleteGroup) {
      onDeleteGroup();
    } else {
      onClose();
    }
  }, [group.id, t, threadId, authUser, onClose, onDeleteGroup]);

  const handleAvatarUpload = useCallback(async (file: File) => {
    setUploadingAvatar(true);
    try {
      const [uploadInfo] = await providers.FileProvider.getUploadUrl({
        fileIds: [{ subjectId: group.id, type: "avatar" }],
      });
      const { urlPart, signedUrlPart } = uploadInfo.fileUpload;
      await new Promise<void>((resolve, reject) => {
        fileUploader.addToQueue([{
          file,
          fileType: getFileMimeType(file),
          urlPart: stripSignature(urlPart),
          id: `group_avatar_${group.id}_${Date.now()}`,
          onSuccess: async () => {
            try {
              await providers.GroupsProvider.setGroupAvatar(group.id, signedUrlPart);
              setLocalAvatarUrl(signedUrlPart);
              if (onMembersChanged) {
                const updated = await providers.GroupsProvider.search({ data: { id: group.id } });
                if (updated.length > 0) onMembersChanged(updated[0]);
              }
              resolve();
            } catch (err) {
              reject(err);
            }
          },
          onError: (error: Error) => reject(error),
        }]);
      });
    } catch (e) {
      console.error("Failed to upload avatar:", e);
    } finally {
      setUploadingAvatar(false);
    }
  }, [group.id, onMembersChanged]);

  const handleRemoveAvatar = useCallback(async () => {
    try {
      await providers.GroupsProvider.removeGroupAvatar(group.id);
      setLocalAvatarUrl(null);
      if (onMembersChanged) {
        const updated = await providers.GroupsProvider.search({ data: { id: group.id } });
        if (updated.length > 0) onMembersChanged(updated[0]);
      }
    } catch (e) {
      console.error("Failed to remove avatar:", e);
    }
  }, [group.id, onMembersChanged]);

  const initial = group.name ? group.name.charAt(0).toUpperCase() : "G";
  const memberCount = group.memberIds?.length || 0;
  const avatarSrc = localAvatarUrl
    ? MediaFilesController.getImageThumbnail(localAvatarUrl, "240p")
    : null;

  return (
    <div className="group-info-panel">
      <div className="group-info-panel__header">
        <button className="group-info-panel__close-btn" onClick={onClose} type="button">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
        <span className="group-info-panel__title">{group.isChannel ? t("Channel Info") : t("Group Info")}</span>
      </div>

      <div className="group-info-panel__avatar-section">
        <div className="group-info-panel__avatar-wrap">
          <div className="group-info-panel__avatar">
            {avatarSrc ? (
              <img src={avatarSrc} alt={group.name} className="group-info-panel__avatar-img" />
            ) : (
              initial
            )}
          </div>
          {isOwner && (
            <>
              <button
                type="button"
                className="group-info-panel__avatar-btn"
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
                title={t("Change avatar")}
              >
                {uploadingAvatar ? "..." : (
                  <svg viewBox="0 0 24 24" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 15.2A3.2 3.2 0 1 1 15.2 12 3.2 3.2 0 0 1 12 15.2zm5.6-10.8H15l-1.44-2H10.44L9 4.4H6.4A2.4 2.4 0 0 0 4 6.8v10.4a2.4 2.4 0 0 0 2.4 2.4h11.2a2.4 2.4 0 0 0 2.4-2.4V6.8a2.4 2.4 0 0 0-2.4-2.4z" fill="currentColor" />
                  </svg>
                )}
              </button>
              {avatarSrc && (
                <button
                  type="button"
                  className="group-info-panel__avatar-remove"
                  onClick={handleRemoveAvatar}
                  title={t("Remove avatar")}
                >
                  ×
                </button>
              )}
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleAvatarUpload(file);
                  e.target.value = "";
                }}
              />
            </>
          )}
        </div>
        {isOwner && editingName ? (
          <div className="group-info-panel__edit-field">
            <input
              ref={nameInputRef}
              className="group-info-panel__edit-input"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveName();
                if (e.key === "Escape") setEditingName(false);
              }}
              disabled={saving}
              placeholder={t("Group name")}
            />
            <div className="group-info-panel__edit-actions">
              <button type="button" onClick={handleSaveName} disabled={saving}>
                {saving ? "..." : t("Save")}
              </button>
              <button type="button" onClick={() => setEditingName(false)}>{t("Cancel")}</button>
            </div>
          </div>
        ) : (
          <div
            className="group-info-panel__group-name"
            onClick={isOwner ? () => { setEditName(group.name || ""); setEditingName(true); } : undefined}
            style={isOwner ? { cursor: "pointer" } : undefined}
            title={isOwner ? t("Click to edit") : undefined}
          >
            {group.name}
            {isOwner && (
              <svg className="group-info-panel__edit-icon" viewBox="0 0 24 24" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 000-1.42l-2.34-2.34a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="currentColor" />
              </svg>
            )}
          </div>
        )}

        {isOwner && editingDesc ? (
          <div className="group-info-panel__edit-field">
            <textarea
              ref={descInputRef}
              className="group-info-panel__edit-textarea"
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSaveDesc(); }
                if (e.key === "Escape") setEditingDesc(false);
              }}
              disabled={saving}
              placeholder={t("Group description")}
              rows={3}
            />
            <div className="group-info-panel__edit-actions">
              <button type="button" onClick={handleSaveDesc} disabled={saving}>
                {saving ? "..." : t("Save")}
              </button>
              <button type="button" onClick={() => setEditingDesc(false)}>{t("Cancel")}</button>
            </div>
          </div>
        ) : (
          <div
            className="group-info-panel__group-desc"
            onClick={isOwner ? () => { setEditDesc(group.description || ""); setEditingDesc(true); } : undefined}
            style={isOwner ? { cursor: "pointer" } : undefined}
            title={isOwner ? t("Click to edit") : undefined}
          >
            {group.description || (isOwner ? t("Add description...") : "")}
            {isOwner && !(group.description) && (
              <svg className="group-info-panel__edit-icon" viewBox="0 0 24 24" width="12" height="12" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 000-1.42l-2.34-2.34a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="currentColor" />
              </svg>
            )}
          </div>
        )}

        <div className="group-info-panel__member-count">
          {memberCount} {group.isChannel ? t("subscribers") : t("members")}
        </div>
      </div>

      {isOwner && (
        <div className="group-info-panel__section">
          <div className="group-info-panel__section-header">
            <span className="group-info-panel__section-title">{t("Settings")}</span>
          </div>
          {group.isChannel ? (
            <>
              {/* Channel settings — simplified */}
              <div className="group-info-panel__toggle-row">
                <span className="group-info-panel__toggle-label">{t("Channel")}</span>
                <span className="group-info-panel__info-badge">{t("Public broadcast")}</span>
              </div>
              <div className="group-info-panel__toggle-row">
                <span className="group-info-panel__toggle-label">{t("Posting")}</span>
                <span className="group-info-panel__info-badge">{t("Owner only")}</span>
              </div>
            </>
          ) : (
            <>
              {/* Group settings — editable toggles */}
              <div className="group-info-panel__toggle-row">
                <span className="group-info-panel__toggle-label">{t("Public Group")}</span>
                <label className="group-info-panel__toggle">
                  <input
                    type="checkbox"
                    checked={localIsPublic}
                    onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                      const newVal = e.target.checked;
                      setLocalIsPublic(newVal);
                      try {
                        const updated = await providers.GroupsProvider.toggleGroupPublic(group.id, newVal);
                        if (onMembersChanged && updated) onMembersChanged(updated);
                      } catch (err) {
                        setLocalIsPublic(!newVal);
                        console.error("Failed to toggle public:", err);
                      }
                    }}
                  />
                  <span className="group-info-panel__toggle-slider" />
                </label>
              </div>
              <div className="group-info-panel__toggle-row">
                <span className="group-info-panel__toggle-label">{t("Video Calls")}</span>
                <label className="group-info-panel__toggle">
                  <input
                    type="checkbox"
                    checked={localCallsEnabled}
                    onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                      const newVal = e.target.checked;
                      setLocalCallsEnabled(newVal);
                      try {
                        const updated = await providers.GroupsProvider.toggleGroupCalls(group.id, newVal);
                        if (onMembersChanged && updated) onMembersChanged(updated);
                      } catch (err) {
                        setLocalCallsEnabled(!newVal);
                        console.error("Failed to toggle calls:", err);
                      }
                    }}
                  />
                  <span className="group-info-panel__toggle-slider" />
                </label>
              </div>
              <div
                className="group-info-panel__toggle-row"
                title={!localCallsEnabled ? t("Enable Video Calls first") : undefined}
              >
                <span className="group-info-panel__toggle-label">
                  {t("Auto-Record Video")}
                </span>
                <label className={`group-info-panel__toggle${!localCallsEnabled ? " __disabled" : ""}`}>
                  <input
                    type="checkbox"
                    checked={localAutoRecord && localCallsEnabled}
                    disabled={!localCallsEnabled}
                    onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                      const newVal = e.target.checked;
                      setLocalAutoRecord(newVal);
                      try {
                        const updated = await providers.GroupsProvider.toggleAutoRecordVideo(group.id, newVal);
                        if (onMembersChanged && updated) onMembersChanged(updated);
                      } catch (err) {
                        setLocalAutoRecord(!newVal);
                        console.error("Failed to toggle auto-record:", err);
                      }
                    }}
                  />
                  <span className="group-info-panel__toggle-slider" />
                </label>
              </div>
              {/* Group Topics (Темы) — owner enables, then manages topics below */}
              <div className="group-info-panel__toggle-row">
                <span className="group-info-panel__toggle-label">{t("Topics")}</span>
                <label className="group-info-panel__toggle">
                  <input
                    type="checkbox"
                    checked={localTopicsEnabled}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleToggleTopics(e.target.checked)}
                  />
                  <span className="group-info-panel__toggle-slider" />
                </label>
              </div>
              {localTopicsEnabled && (
                <div className="group-info-panel__topics">
                  {topics.map((topic) => (
                    <div key={topic.id} className="group-info-panel__topic-row">
                      {editingTopicId === topic.id ? (
                        <>
                          <input
                            className="group-info-panel__topic-input"
                            value={editingTopicName}
                            autoFocus
                            maxLength={50}
                            onChange={(e) => setEditingTopicName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleRenameTopic(topic.id);
                              if (e.key === "Escape") setEditingTopicId(null);
                            }}
                          />
                          <button type="button" className="group-info-panel__topic-action" onClick={() => handleRenameTopic(topic.id)}>
                            {t("Save")}
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="group-info-panel__topic-name">{topic.name}</span>
                          <button
                            type="button"
                            className="group-info-panel__topic-action"
                            title={t("Rename")}
                            onClick={() => { setEditingTopicId(topic.id); setEditingTopicName(topic.name); }}
                          >
                            {t("Rename")}
                          </button>
                          <button
                            type="button"
                            className="group-info-panel__topic-action group-info-panel__topic-action--danger"
                            title={t("Delete")}
                            onClick={() => handleDeleteTopic(topic)}
                          >
                            {t("Delete")}
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                  <div className="group-info-panel__topic-create">
                    <input
                      className="group-info-panel__topic-input"
                      placeholder={t("New topic name")}
                      value={newTopicName}
                      maxLength={50}
                      onChange={(e) => setNewTopicName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleCreateTopic(); }}
                    />
                    <button
                      type="button"
                      className="group-info-panel__topic-action"
                      disabled={!newTopicName.trim() || topicBusy}
                      onClick={handleCreateTopic}
                    >
                      {t("Add")}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          <div className="group-info-panel__invite-row">
            <span className="group-info-panel__toggle-label">{t("Invite")}</span>
            <button
              type="button"
              className="group-info-panel__invite-btn"
              onClick={handleCopyInviteLink}
            >
              {linkCopied ? t("Copied!") : t("Copy")}
            </button>
          </div>
        </div>
      )}

      <div className="group-info-panel__section group-info-panel__section--members">
        <div className="group-info-panel__section-header">
          <span className="group-info-panel__section-title">{group.isChannel ? t("Subscribers") : t("Members")}</span>
          {isOwner && (
            <button
              className="group-info-panel__add-btn"
              onClick={() => setShowAddMember(!showAddMember)}
              type="button"
            >
              {showAddMember ? t("Cancel") : t("Add")}
            </button>
          )}
        </div>

        {showAddMember && (
          <>
            <div className="group-info-panel__add-member-input">
              <input
                type="text"
                placeholder={t("Search users...")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
            {searchResults.length > 0 && (
              <div className="group-info-panel__search-results">
                {searchResults.map((user) => (
                  <div
                    key={user.id}
                    className="group-info-panel__search-item"
                    onClick={() => handleAddMember(String(user.id))}
                  >
                    <BaseAvatar user={user} size={32} />
                    <span className="group-info-panel__search-item-name">{user.screenName}</span>
                  </div>
                ))}
              </div>
            )}
            {searching && (
              <div className="group-info-panel__searching">{t("Searching...")}</div>
            )}
          </>
        )}

        {!showAddMember && (
          <div className="group-info-panel__members-list">
            {members.map((member) => (
              <div key={member.id} className="group-info-panel__member-item">
                <BaseAvatar user={member} size={36} />
                <div className="group-info-panel__member-info">
                  <span className="group-info-panel__member-name">{member.screenName}</span>
                  {String(member.id) === group.ownerId && (
                    <span className="group-info-panel__member-role">{t("Owner")}</span>
                  )}
                </div>
                {isOwner && String(member.id) !== group.ownerId && (
                  <button
                    className="group-info-panel__member-remove"
                    onClick={() => handleRemoveMember(String(member.id))}
                    type="button"
                    title={t("Remove")}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="group-info-panel__leave-section">
        {!isOwner && (
          <button className="group-info-panel__leave-btn" onClick={handleLeaveGroup} type="button">
            {group.isChannel ? t("Unsubscribe") : t("Leave Group")}
          </button>
        )}
        {isOwner && (
          <button className="group-info-panel__leave-btn __danger" onClick={handleDeleteGroup} type="button">
            {group.isChannel ? t("Delete Channel") : t("Delete Group")}
          </button>
        )}
      </div>
    </div>
  );
}

export default GroupInfoPanel;
